import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { normalizeRole } from '../utils/userLocale';
import { brandingFromUser } from '../utils/tenantBranding';
import { exportTableToPdf } from '../utils/reportExport';
import {
  addDays,
  dateKeyFromString,
  getLocalDateKey,
  weekInputFromDateKey,
  weekRangeFromInput,
} from '../utils/reportDates';
import {
  buildFinancialReport,
  financialReportLabels,
  formatTzs,
  saleProfit,
} from '../utils/financialReportData';
import './BusinessFinancialReportPage.css';

const PERIODS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'custom', label: 'Custom range' },
];

function monthEndDate(monthKey) {
  const [y, m] = String(monthKey).split('-').map(Number);
  const last = new Date(y, m || 1, 0).getDate();
  return `${monthKey}-${String(last).padStart(2, '0')}`;
}

function resolveRange(period, { anchorDate, weekInput, monthKey, fromDate, toDate }) {
  const today = getLocalDateKey();
  if (period === 'day') {
    const d = anchorDate || today;
    return { fromDate: d, toDate: d, label: d };
  }
  if (period === 'week') {
    const { fromDate: from, toDate: to } = weekRangeFromInput(weekInput || weekInputFromDateKey(today));
    return { fromDate: from, toDate: to, label: `${from} → ${to}` };
  }
  if (period === 'month') {
    const m = monthKey || today.slice(0, 7);
    return { fromDate: `${m}-01`, toDate: monthEndDate(m), label: m };
  }
  const from = fromDate || today;
  const to = toDate && toDate >= from ? toDate : from;
  return { fromDate: from, toDate: to, label: `${from} → ${to}` };
}

function formatStatus(status) {
  return String(status || 'pending')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function BusinessFinancialReportPage({ initialPeriod = 'month', onNavigate }) {
  const { user } = useAuth();
  const labels = useMemo(() => financialReportLabels(user), [user]);
  const isOwner = normalizeRole(user?.role) === 'owner';
  const today = getLocalDateKey();

  const [period, setPeriod] = useState(initialPeriod);
  const [anchorDate, setAnchorDate] = useState(today);
  const [weekInput, setWeekInput] = useState(weekInputFromDateKey(today));
  const [monthKey, setMonthKey] = useState(today.slice(0, 7));
  const [fromDate, setFromDate] = useState(addDays(today, -30));
  const [toDate, setToDate] = useState(today);

  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const range = useMemo(
    () => resolveRange(period, { anchorDate, weekInput, monthKey, fromDate, toDate }),
    [period, anchorDate, weekInput, monthKey, fromDate, toDate]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    const from = range.fromDate;
    const to = range.toDate;
    try {
      const salesParams = new URLSearchParams();
      if (from) salesParams.set('from', from);
      if (to) salesParams.set('to', to);

      const tasks = [
        apiJson(`/sales?${salesParams}`),
        apiJson('/expenses').catch(() => ({ expenses: [] })),
      ];
      if (isOwner) {
        tasks.push(apiJson('/orders').catch(() => ({ orders: [] })));
      } else {
        tasks.push(apiJson('/my-orders').catch(() => ({ orders: [] })));
      }

      const [salesData, expensesData, ordersData] = await Promise.all(tasks);
      setSales(Array.isArray(salesData.sales) ? salesData.sales : []);
      setExpenses(Array.isArray(expensesData.expenses) ? expensesData.expenses : []);
      setOrders(Array.isArray(ordersData.orders) ? ordersData.orders : []);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load report data.');
      setSales([]);
      setExpenses([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [range.fromDate, range.toDate, isOwner]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const report = useMemo(
    () =>
      buildFinancialReport({
        sales,
        expenses,
        orders,
        fromDate: range.fromDate,
        toDate: range.toDate,
        user,
      }),
    [sales, expenses, orders, range.fromDate, range.toDate, user]
  );

  const handleExportPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const summaryRows = [
        ['Sales revenue', formatTzs(report.salesRevenue)],
        ['Sales profit (gross)', formatTzs(report.salesProfit)],
        ['Purchases (orders)', formatTzs(report.purchasesTotal)],
        ['Expenses', formatTzs(report.expensesTotal)],
        ['Net profit', formatTzs(report.netProfit)],
      ];
      await exportTableToPdf(
        `${labels.title} — ${range.label}`,
        ['Metric', 'Amount'],
        summaryRows,
        {
          branding: brandingFromUser(user),
          criteria: [{ label: 'Period', value: range.label }],
          summary: [
            { label: 'Sales', value: report.salesCount },
            { label: 'Purchase orders', value: report.purchasesCount },
            { label: 'Expense lines', value: report.expenseRows.length },
          ],
        }
      );
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="fin-report page-bleed">
      <header className="fin-report__hero">
        <div>
          <h1>{labels.title}</h1>
          <p>{labels.subtitle}</p>
        </div>
        <div className="fin-report__hero-actions">
          {typeof onNavigate === 'function' ? (
            <button type="button" className="fin-report__btn fin-report__btn--ghost" onClick={() => onNavigate('reports')}>
              ← All reports
            </button>
          ) : null}
          <button
            type="button"
            className="fin-report__btn fin-report__btn--primary"
            onClick={() => void handleExportPdf()}
            disabled={loading || pdfBusy}
          >
            {pdfBusy ? 'PDF…' : 'Download PDF'}
          </button>
        </div>
      </header>

      <div className="fin-report__period-tabs" role="tablist" aria-label="Report period">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={period === p.key}
            className={`fin-report__period-tab${period === p.key ? ' fin-report__period-tab--active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="fin-report__filters">
        {period === 'day' ? (
          <label className="fin-report__filter">
            <span>Date</span>
            <input type="date" value={anchorDate} max={today} onChange={(e) => setAnchorDate(e.target.value)} />
          </label>
        ) : null}
        {period === 'week' ? (
          <label className="fin-report__filter">
            <span>Week</span>
            <input type="week" value={weekInput} onChange={(e) => setWeekInput(e.target.value)} />
          </label>
        ) : null}
        {period === 'month' ? (
          <label className="fin-report__filter">
            <span>Month</span>
            <input type="month" value={monthKey} max={today.slice(0, 7)} onChange={(e) => setMonthKey(e.target.value)} />
          </label>
        ) : null}
        {period === 'custom' ? (
          <>
            <label className="fin-report__filter">
              <span>From</span>
              <input type="date" value={fromDate} max={toDate || today} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="fin-report__filter">
              <span>To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
          </>
        ) : null}
        <button type="button" className="fin-report__btn fin-report__btn--ghost" onClick={() => void loadData()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <p className="fin-report__range-label">
        Showing: <strong>{range.label}</strong>
      </p>

      {error ? (
        <p className="fin-report__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="fin-report__kpis" aria-busy={loading}>
        <article className="fin-report__kpi fin-report__kpi--sales">
          <span className="fin-report__kpi-label">{labels.sales}</span>
          <strong className="fin-report__kpi-value">{formatTzs(report.salesRevenue)}</strong>
          <span className="fin-report__kpi-meta">
            {report.salesCount} sale{report.salesCount !== 1 ? 's' : ''} · Profit {formatTzs(report.salesProfit)}
          </span>
          <span className="fin-report__kpi-hint">{labels.salesHint}</span>
        </article>
        <article className="fin-report__kpi fin-report__kpi--purchases">
          <span className="fin-report__kpi-label">{labels.purchases}</span>
          <strong className="fin-report__kpi-value">{formatTzs(report.purchasesTotal)}</strong>
          <span className="fin-report__kpi-meta">
            {report.purchasesCount} order{report.purchasesCount !== 1 ? 's' : ''}
          </span>
          <span className="fin-report__kpi-hint">{labels.purchasesHint}</span>
        </article>
        <article className="fin-report__kpi fin-report__kpi--expenses">
          <span className="fin-report__kpi-label">{labels.expenses}</span>
          <strong className="fin-report__kpi-value">{formatTzs(report.expensesTotal)}</strong>
          <span className="fin-report__kpi-meta">
            Boss {formatTzs(report.expensesBoss)} · Staff {formatTzs(report.expensesStaff)} · Office{' '}
            {formatTzs(report.expensesOffice)}
          </span>
          <span className="fin-report__kpi-hint">{labels.expensesHint}</span>
        </article>
        <article
          className={`fin-report__kpi fin-report__kpi--profit${report.netProfit < 0 ? ' fin-report__kpi--loss' : ''}`}
        >
          <span className="fin-report__kpi-label">{labels.profit}</span>
          <strong className="fin-report__kpi-value">{formatTzs(report.netProfit)}</strong>
          <span className="fin-report__kpi-meta">Sales profit − expenses</span>
          <span className="fin-report__kpi-hint">{labels.profitHint}</span>
        </article>
      </div>

      <section className="fin-report__section">
        <h2>Sales detail</h2>
        {report.salesRows.length === 0 ? (
          <p className="fin-report__empty">No sales in this period.</p>
        ) : (
          <div className="fin-report__table-wrap">
            <table className="fin-report__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>{labels.productCol}</th>
                  <th>Qty</th>
                  <th>Revenue</th>
                  <th>Profit</th>
                </tr>
              </thead>
              <tbody>
                {report.salesRows.map((s) => (
                  <tr key={s.id}>
                    <td>{dateKeyFromString(s.date)}</td>
                    <td>{s.product?.name || '—'}</td>
                    <td>{s.quantity}</td>
                    <td>{formatTzs(s.amount)}</td>
                    <td className={saleProfit(s) < 0 ? 'fin-report__neg' : ''}>{formatTzs(saleProfit(s))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td>{formatTzs(report.salesRevenue)}</td>
                  <td>{formatTzs(report.salesProfit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <section className="fin-report__section">
        <h2>Purchases detail</h2>
        {report.purchaseRows.length === 0 ? (
          <p className="fin-report__empty">No purchase orders in this period.</p>
        ) : (
          <div className="fin-report__table-wrap">
            <table className="fin-report__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>PO</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.purchaseRows.map((o) => (
                  <tr key={o.id}>
                    <td>{orderDateKey(o)}</td>
                    <td>{o.po_number || `PO-${o.id}`}</td>
                    <td>{o.supplier_name || o.supplier?.name || '—'}</td>
                    <td>
                      <span className="fin-report__status">{formatStatus(o.status)}</span>
                    </td>
                    <td>{formatTzs(o.estimated_total_tzs ?? o.estimatedTotalTzs)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Total</td>
                  <td>{formatTzs(report.purchasesTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <section className="fin-report__section">
        <h2>Expenses detail</h2>
        {report.expenseRows.length === 0 ? (
          <p className="fin-report__empty">No expenses recorded in this period.</p>
        ) : (
          <div className="fin-report__table-wrap">
            <table className="fin-report__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.expenseRows.map((e) => (
                  <tr key={e.id}>
                    <td>{expenseDateKey(e)}</td>
                    <td>{String(e.category || '—').replace(/^\w/, (c) => c.toUpperCase())}</td>
                    <td>{e.description || e.title || '—'}</td>
                    <td>{formatTzs(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td>{formatTzs(report.expensesTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function orderDateKey(order) {
  return dateKeyFromString(order?.created_at || order?.createdAt);
}

function expenseDateKey(expense) {
  return String(expense?.expenseDate || expense?.expense_date || '').slice(0, 10);
}
